import { useEffect, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, Save, Upload, Copy } from "lucide-react";
import { BUSINESS_TYPE_LIST, DEFAULT_BUSINESS_TYPE } from "@/config/businessTypes";

const DAYS = [
  { k: "monday", l: "Lunes" },
  { k: "tuesday", l: "Martes" },
  { k: "wednesday", l: "Miércoles" },
  { k: "thursday", l: "Jueves" },
  { k: "friday", l: "Viernes" },
  { k: "saturday", l: "Sábado" },
  { k: "sunday", l: "Domingo" },
];

function Section({ title, subtitle, children }) {
  return (
    <section className="bg-surface gold-border rounded-2xl p-6 md:p-8 mb-6">
      <div className="mb-6">
        <p className="overline mb-1">{subtitle}</p>
        <h2 className="font-display text-2xl md:text-3xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function Settings() {
  const [biz, setBiz] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/business/me").then(({ data }) => setBiz(data)).catch(() => {});
  }, []);

  if (!biz) return <OwnerLayout><div className="text-neutral-500">Cargando…</div></OwnerLayout>;

  const update = (patch) => setBiz({ ...biz, ...patch });

  const updateService = (i, patch) => {
    const services = [...(biz.services || [])];
    services[i] = { ...services[i], ...patch };
    update({ services });
  };
  const removeService = (i) => update({ services: biz.services.filter((_, idx) => idx !== i) });
  const addService = () =>
    update({ services: [...(biz.services || []), { id: "", name: "", price: 0, duration: 60 }] });

  const updateSchedule = (day, patch) =>
    update({ schedule: { ...biz.schedule, [day]: { ...biz.schedule[day], ...patch } } });

  const onLogoFile = (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Logo muy grande (máx 500 KB)"); return; }
    const reader = new FileReader();
    reader.onload = () => update({ logo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: biz.name,
        logo_base64: biz.logo_base64,
        whatsapp: biz.whatsapp,
        address: biz.address,
        schedule: biz.schedule,
        services: biz.services,
        deposit_percent: Number(biz.deposit_percent) || 0,
        payment_info: biz.payment_info,
        business_type: biz.business_type || DEFAULT_BUSINESS_TYPE,
      };
      const { data } = await api.put("/business/me", payload);
      setBiz(data);
      toast.success("Cambios guardados");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  const publicUrl = `${window.location.origin}/agenda/${biz.slug}`;
  const copyUrl = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copiado"); };

  return (
    <OwnerLayout>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">Ajustes del negocio</p>
          <h1 className="font-display text-4xl md:text-5xl" data-testid="settings-title">Configuración</h1>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-gold text-void hover:bg-gold/90 rounded-full font-bold h-12 px-6"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>

      {/* Public link */}
      <div className="bg-void gold-border rounded-2xl p-4 md:p-5 mb-6 flex flex-col md:flex-row items-start md:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="overline mb-1">Tu link público</div>
          <div className="font-mono text-cyan text-sm truncate" data-testid="public-url">{publicUrl}</div>
        </div>
        <Button variant="outline" onClick={copyUrl} className="border-cyan/40 text-cyan hover:bg-cyan/10 rounded-full" data-testid="copy-url-btn">
          <Copy className="w-4 h-4 mr-2" /> Copiar link
        </Button>
      </div>

      {/* Business info */}
      <Section title="Datos del negocio" subtitle="Identidad">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Tipo de negocio</Label>
            <Select
              value={biz.business_type || DEFAULT_BUSINESS_TYPE}
              onValueChange={(v) => update({ business_type: v })}
            >
              <SelectTrigger className="mt-2 bg-void border-gold/20 h-12" data-testid="settings-business-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-gold/20">
                {BUSINESS_TYPE_LIST.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Nombre del negocio</Label>
            <Input value={biz.name} onChange={(e) => update({ name: e.target.value })}
              className="mt-2 bg-void border-gold/20 h-12" data-testid="settings-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-400">WhatsApp (con lada)</Label>
            <Input value={biz.whatsapp} onChange={(e) => update({ whatsapp: e.target.value })}
              placeholder="+527297553698"
              className="mt-2 bg-void border-gold/20 h-12" data-testid="settings-whatsapp" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Dirección</Label>
            <Textarea value={biz.address} onChange={(e) => update({ address: e.target.value })}
              className="mt-2 bg-void border-gold/20 min-h-[64px]" data-testid="settings-address" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Logo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-void gold-border flex items-center justify-center overflow-hidden">
                {biz.logo_base64 ? (
                  <img src={biz.logo_base64} alt="logo" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-neutral-600 text-xs">Sin logo</span>
                )}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" hidden onChange={(e) => onLogoFile(e.target.files?.[0])} data-testid="logo-input" />
                <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-void gold-border hover:border-gold text-sm text-neutral-200">
                  <Upload className="w-4 h-4" /> Subir logo
                </span>
              </label>
              {biz.logo_base64 && (
                <button onClick={() => update({ logo_base64: "" })} className="text-neonpink text-xs uppercase tracking-widest">Quitar</button>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* Schedule */}
      <Section title="Horarios" subtitle="Lunes a domingo">
        <div className="space-y-3">
          {DAYS.map((d) => {
            const s = biz.schedule?.[d.k] || { closed: false, open: "09:00", close: "18:00" };
            return (
              <div key={d.k} className="grid grid-cols-12 items-center gap-3 py-2 border-b border-gold/5 last:border-0">
                <div className="col-span-4 md:col-span-3 font-medium text-neutral-200">{d.l}</div>
                <div className="col-span-4 md:col-span-3 flex items-center gap-2">
                  <Switch
                    checked={!s.closed}
                    onCheckedChange={(v) => updateSchedule(d.k, { closed: !v })}
                    data-testid={`sched-${d.k}-open`}
                  />
                  <span className="text-xs uppercase tracking-widest text-neutral-500">
                    {s.closed ? "Cerrado" : "Abierto"}
                  </span>
                </div>
                <div className="col-span-2 md:col-span-3">
                  <Input type="time" value={s.open} onChange={(e) => updateSchedule(d.k, { open: e.target.value })}
                    disabled={s.closed}
                    className="bg-void border-gold/20 h-11" data-testid={`sched-${d.k}-open-time`} />
                </div>
                <div className="col-span-2 md:col-span-3">
                  <Input type="time" value={s.close} onChange={(e) => updateSchedule(d.k, { close: e.target.value })}
                    disabled={s.closed}
                    className="bg-void border-gold/20 h-11" data-testid={`sched-${d.k}-close-time`} />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Services */}
      <Section title="Servicios" subtitle="Nombre · precio · duración">
        <div className="space-y-3">
          {(biz.services || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 md:gap-3 items-center">
              <Input
                className="col-span-12 md:col-span-5 bg-void border-gold/20 h-11"
                placeholder="Nombre" value={s.name}
                onChange={(e) => updateService(i, { name: e.target.value })}
                data-testid={`svc-name-${i}`}
              />
              <Input
                className="col-span-6 md:col-span-3 bg-void border-gold/20 h-11"
                type="number" placeholder="Precio" value={s.price}
                onChange={(e) => updateService(i, { price: Number(e.target.value) })}
                data-testid={`svc-price-${i}`}
              />
              <Input
                className="col-span-4 md:col-span-3 bg-void border-gold/20 h-11"
                type="number" placeholder="Duración (min)" value={s.duration}
                onChange={(e) => updateService(i, { duration: Number(e.target.value) })}
                data-testid={`svc-duration-${i}`}
              />
              <button onClick={() => removeService(i)} className="col-span-2 md:col-span-1 text-neonpink flex justify-center" data-testid={`svc-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button variant="ghost" onClick={addService} className="text-cyan hover:text-gold" data-testid="add-service-btn">
            <Plus className="w-4 h-4 mr-1" /> Agregar servicio
          </Button>
        </div>
      </Section>

      {/* Deposit + Payment info */}
      <Section title="Cobro de anticipo" subtitle="Reserva garantizada">
        <div className="max-w-sm">
          <Label className="text-xs uppercase tracking-widest text-neutral-400">% de anticipo</Label>
          <Input
            type="number" min={0} max={100}
            value={biz.deposit_percent}
            onChange={(e) => update({ deposit_percent: Number(e.target.value) })}
            className="mt-2 bg-void border-gold/20 h-12" data-testid="settings-deposit-percent"
          />
        </div>
      </Section>

      {/* Bank transfer info */}
      <Section title="Datos para transferencia bancaria" subtitle="Se muestran en la reserva pública">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { k: "bank", l: "Banco", ph: "Ej: BBVA, Santander, Banorte…", tid: "settings-bank" },
            { k: "account_holder", l: "Titular", ph: "Nombre completo", tid: "settings-holder" },
            { k: "clabe", l: "CLABE", ph: "18 dígitos", mono: true, tid: "settings-clabe" },
            { k: "account_number", l: "Número de cuenta", ph: "Opcional", mono: true, tid: "settings-account-number" },
            { k: "card_number", l: "Número de tarjeta", ph: "Opcional", mono: true, tid: "settings-card-number" },
            { k: "reference", l: "Referencia", ph: "Opcional (concepto sugerido)", tid: "settings-reference" },
          ].map((f) => (
            <div key={f.k}>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">{f.l}</Label>
              <Input
                value={biz.payment_info?.[f.k] || ""}
                onChange={(e) => update({ payment_info: { ...biz.payment_info, [f.k]: e.target.value } })}
                placeholder={f.ph}
                className={`mt-2 bg-void border-gold/20 h-12 ${f.mono ? "font-mono" : ""}`}
                data-testid={f.tid}
              />
            </div>
          ))}
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Instrucciones adicionales</Label>
            <Textarea
              value={biz.payment_info?.additional_instructions || ""}
              onChange={(e) => update({ payment_info: { ...biz.payment_info, additional_instructions: e.target.value } })}
              placeholder="Ej: envía tu comprobante por WhatsApp una vez hecha la transferencia."
              className="mt-2 bg-void border-gold/20 min-h-[80px]"
              data-testid="settings-additional-instructions"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-neutral-400">Link Mercado Pago (opcional)</Label>
            <Input
              value={biz.payment_info?.mp_link || ""}
              onChange={(e) => update({ payment_info: { ...biz.payment_info, mp_link: e.target.value } })}
              placeholder="https://mpago.la/..."
              className="mt-2 bg-void border-gold/20 h-12" data-testid="settings-mp"
            />
          </div>
        </div>
      </Section>

      <Button
        onClick={save}
        disabled={saving}
        className="bg-gold text-void hover:bg-gold/90 rounded-full font-bold h-12 px-8"
        data-testid="save-settings-btn-bottom"
      >
        <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando…" : "Guardar cambios"}
      </Button>
    </OwnerLayout>
  );
}
